import React, { useState, useEffect } from 'react';
import { GetAll, apiURL } from '../../utils/apiCalls';

// Cache for grid ads data to avoid redundant API calls
const gridAdsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Grid Ads Component
 * Displays maximum 6 ads in a 2x3 grid (2 rows, 3 columns - one card per ad) by default
 * If more than 6 ads exist, randomly selects 6 and shuffles them
 * Rotates to new random selection every 10 seconds
 * Optimized with caching for better performance
 * @param {number} positionId - The ad position ID to filter ads
 * @param {number} rows - Number of rows to display (default: 2, max: 2)
 */
const GridAds = ({ positionId, rows = 2 }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAds, setCurrentAds] = useState([]); // Randomly selected 6 ads (3 per column)

  useEffect(() => {
    const fetchAds = async () => {
      if (!positionId) {
        setLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `grid_position_${positionId}`;
      const cached = gridAdsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setAds(cached.data);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await GetAll(apiURL + '/api/v1/ads');
        
        console.log('GridAds - API Response:', response);
        
        if (response && response.data && response.data.status) {
          const allAds = response.data.data || [];
          console.log('GridAds - All ads:', allAds);
          console.log('GridAds - Looking for positionId:', positionId);
          
          const filteredAds = allAds.filter(ad => String(ad.position_id) === String(positionId));
          console.log('GridAds - Filtered ads for position:', filteredAds);
          
          if (filteredAds.length === 0) {
            console.log('GridAds - No ads found for positionId:', positionId);
            setLoading(false);
            gridAdsCache.set(cacheKey, { data: [], timestamp: Date.now() });
            return;
          }
          
          // Batch fetch uploads for all ads in parallel (optimized)
          // Use Promise.allSettled to handle partial failures gracefully
          const uploadPromises = filteredAds.map(async (ad) => {
            try {
              // Use the correct endpoint: /api/v1/ads/:id/uploads
              // Assume one ad = one media, so get the first upload
              const assetsResponse = await GetAll(apiURL + `/api/v1/ads/${ad.id}/uploads`);
              console.log(`GridAds - Uploads for ad ${ad.id}:`, assetsResponse);
              const uploads = assetsResponse?.data?.data || [];
              // Convert Bookshelf collection to array if needed
              const allAssets = Array.isArray(uploads) ? uploads : (uploads.toJSON ? uploads.toJSON() : []);
              
              // Filter to get the first valid asset (prefer completed, fallback to uploading)
              let asset = null;
              if (allAssets.length > 0) {
                const completedAssets = allAssets.filter(a => a.status === 'completed' && a.filepaths3);
                const uploadingAssets = allAssets.filter(a => a.status === 'uploading' && a.filepaths3);
                // Prefer completed, but use uploading if no completed exists
                asset = completedAssets[0] || uploadingAssets[0] || (allAssets[0]?.filepaths3 ? allAssets[0] : null);
              }
              
              // Store as array with single asset (for compatibility with existing code)
              const assets = asset ? [asset] : [];
              console.log(`GridAds - Processed asset for ad ${ad.id}:`, asset);
              return { ...ad, assets };
            } catch (err) {
              console.error(`Error fetching uploads for ad ${ad.id}:`, err);
              return { ...ad, assets: [] };
            }
          });
          
          const results = await Promise.allSettled(uploadPromises);
          const adsWithAssets = results
            .filter(result => result.status === 'fulfilled')
            .map(result => result.value);
          
          console.log('GridAds - Ads with assets:', adsWithAssets);
          
          // Filter ads to only include those with valid uploads
          // Assume one ad = one media (one asset)
          const validAds = adsWithAssets.filter(ad => {
            if (!ad.assets || ad.assets.length === 0) {
              console.log(`GridAds - Ad ${ad.id} has no assets`);
              return false;
            }
            
            // Get the first (and only) asset for this ad
            const asset = ad.assets[0];
            
            // Check if asset has required fields
            if (!asset || !asset.filepaths3) {
              console.log(`GridAds - Ad ${ad.id} asset missing filepaths3 URL`);
              return false;
            }
            
            // Check status if it exists (prefer 'completed' or 'uploading')
            if (asset.hasOwnProperty('status')) {
              const validStatuses = ['completed', 'uploading'];
              if (!validStatuses.includes(asset.status)) {
                console.log(`GridAds - Ad ${ad.id} asset status is not valid:`, asset.status);
                return false;
              }
            }
            
            return true;
          });
          
          console.log('GridAds - Valid ads after filtering:', validAds);
          
          // Cache the results
          gridAdsCache.set(cacheKey, { data: validAds, timestamp: Date.now() });
          setAds(validAds);
        } else {
          console.log('GridAds - API response error:', response);
        }
      } catch (error) {
        console.error('Error fetching grid ads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [positionId]);

  /**
   * Get session identifier for personalized ad selection
   * Since ads are displayed on public pages (before login), we always use session ID
   * 
   * This ensures:
   * - Different browser sessions see different ads
   * - Same browser session sees consistent ads (on page reload)
   * - Each user/browser gets personalized ad selection
   * 
   * Session ID persists across page reloads but is unique per browser
   */
  const getUserIdentifier = () => {
    // Always use session ID since ads are shown before login
    // Get existing session ID or generate a new one
    let sessionId = sessionStorage.getItem('grid_ads_session_id');
    
    if (!sessionId) {
      // Generate a unique session ID for this browser session
      // This persists across page reloads but is unique per browser
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('grid_ads_session_id', sessionId);
    }
    
    return sessionId;
  };

  /**
   * Simple seeded random number generator
   * Uses a seed to generate consistent "random" numbers for the same seed
   * Different seeds produce different sequences
   */
  const seededRandom = (seed) => {
    let value = seed;
    return () => {
      value = (value * 9301 + 49297) % 233280;
      return value / 233280;
    };
  };

  // Randomly select 6 ads (2 rows x 3 columns) and rotate every 10 seconds
  useEffect(() => {
    if (ads.length === 0) return;

    // Get user identifier to personalize ad selection
    const userIdentifier = getUserIdentifier();
    console.log('GridAds - User identifier:', userIdentifier);

    /**
     * Seeded Fisher-Yates shuffle algorithm
     * Uses user identifier to generate different shuffles for different users
     * Same user will see same shuffle in same session, different users see different ads
     * 
     * How it works:
     * 1. Convert user identifier to a numeric seed (hash)
     * 2. Use seeded random number generator instead of Math.random()
     * 3. This ensures same user always gets same shuffle, different users get different shuffles
     */
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash);
    };

    /**
     * Fisher-Yates shuffle with user-specific seeding
     * 
     * PROBLEM with old method: Math.random() without seed
     * - All users could see same ads if they visit at same time
     * - No personalization based on user
     * 
     * SOLUTION: Seeded shuffle based on user identifier
     * - Each user gets different but consistent shuffle
     * - Same user sees same ads in same session (cache consistency)
     * - Different users see different ads (personalization)
     * - Rotation time added to seed for variety over time
     * 
     * How it works:
     * 1. Create seed from user identifier + rotation index
     * 2. Use seeded random instead of Math.random()
     * 3. Each user gets unique shuffle sequence
     */
    const shuffleArrayWithSeed = (array, seed) => {
      const shuffled = [...array]; // Create a copy to avoid mutating original
      const random = seededRandom(seed);
      
      for (let i = shuffled.length - 1; i > 0; i--) {
        // Use seeded random instead of Math.random()
        const j = Math.floor(random() * (i + 1));
        // Swap elements at positions i and j
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    /**
     * Randomly select up to 6 ads from the available ads (user-specific)
     * 
     * Selection Logic:
     * - If 6 or fewer ads: Shuffle all ads using user seed and return them
     * - If more than 6 ads: Shuffle all ads using user seed, then take first 6
     * 
     * User Personalization:
     * - Each user gets different but consistent ad selection
     * - Uses user ID or session ID as seed for shuffle
     * - Same user sees same ads in same session (cache consistency)
     * - Different users see different ads (personalization)
     * - Adds rotation index to seed for variety over time
     * 
     * Probability Distribution:
     * 1. Each ad has equal chance to be selected
     *    - If 10 ads: Each ad has 6/10 = 60% chance to be in top 6
     *    - If 20 ads: Each ad has 6/20 = 30% chance to be in top 6
     * 
     * 2. Each selected ad has equal chance to be in any position
     *    - Due to seeded Fisher-Yates shuffle, all positions are equally likely
     * 
     * 3. Different users get different selections
     *    - User-specific seed ensures personalization
     * 
     * Example scenarios:
     * - User A with ID 123 → Gets ads [C, A, E, B, F, D] based on seed from user_123
     * - User B with ID 456 → Gets ads [F, C, A, D, B, E] based on seed from user_456
     * - Anonymous session → Gets ads based on session ID seed
     * - Same user on reload → Sees same ads (consistent experience)
     */
    let rotationIndex = 0;
    const selectRandomAds = () => {
      if (ads.length === 0) return [];
      
      // Create seed from user identifier + rotation index for variety over time
      // This ensures different rotations show different ads for same user
      const baseSeed = hashString(userIdentifier);
      const seed = baseSeed + rotationIndex;
      
      // Shuffle all ads using user-specific seeded Fisher-Yates algorithm
      const shuffled = shuffleArrayWithSeed(ads, seed);
      
      // Increment rotation index for next rotation
      rotationIndex++;
      
      // Calculate max ads based on rows prop (3 ads per row)
      const maxAds = rows === 1 ? 3 : 6;
      
      // If we have maxAds or fewer ads, return all shuffled ads
      if (ads.length <= maxAds) {
        return shuffled;
      }
      
      // If we have more than maxAds ads, return first maxAds from shuffled array
      // This gives each ad equal probability of being selected (maxAds/total)
      // But the selection is personalized per user
      return shuffled.slice(0, maxAds);
    };

    // Set initial random selection when ads are loaded
    // This will be personalized based on user identifier
    const initialAds = selectRandomAds();
    setCurrentAds(initialAds);
    console.log(`GridAds - Initial selection for ${userIdentifier}: ${initialAds.length} ads (max 6) from ${ads.length} total ads`);

    // Rotate to new random selection every 10 seconds
    // This ensures users see different ads over time, increasing engagement
    // The shuffle ensures each rotation shows a different combination
    const interval = setInterval(() => {
      const newRandomAds = selectRandomAds();
      setCurrentAds(newRandomAds);
      console.log(`GridAds - Rotated to new selection: ${newRandomAds.length} ads (max 6)`);
    }, 10000); // 10 seconds - configurable interval for rotation

    // Cleanup interval when component unmounts or ads change
    return () => clearInterval(interval);
  }, [ads, rows]);

  if (loading || ads.length === 0) {
    return null;
  }
  
  // Ensure we have at least one ad to display
  if (currentAds.length === 0) {
    return null;
  }
  
  // Split into rows (3 ads per row)
  // Row 1: first 3 ads (indices 0-2)
  // Row 2: next 3 ads (indices 3-5) - only if rows > 1
  const row1 = currentAds.slice(0, 3);
  const row2 = rows > 1 ? currentAds.slice(3, 6) : [];
  
  console.log(`GridAds - Displaying: Row 1 ${row1.length} ads${rows > 1 ? `, Row 2 ${row2.length} ads` : ''} (Total: ${currentAds.length} ads in ${rows}x3 grid)`);

  const renderAdItem = (ad, index) => {
    // Assume one ad = one media, get the first (and only) asset
    const asset = ad?.assets?.[0];
    
    if (!asset || !asset.filepaths3) {
      console.log(`GridAds - No valid asset found for ad ${ad.id}`);
      return null;
    }

    console.log(`GridAds - Rendering ad ${ad.id} with asset:`, asset);

    // Use file_ext to determine file type (from database schema)
    const fileExt = asset.file_ext?.toLowerCase() || '';
    const fileName = asset.file_name?.toLowerCase() || '';
    
    const isVideo = /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(fileExt) || 
                    /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(fileName);
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileExt) || 
                    /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
    
    // Use filepaths3 from database schema
    const assetUrl = asset.filepaths3;
    
    console.log(`GridAds - Asset URL for ad ${ad.id}:`, assetUrl, 'isVideo:', isVideo, 'isImage:', isImage);
    
    if (!assetUrl) {
      console.log(`GridAds - No assetUrl for ad ${ad.id}`);
      return null;
    }

    const handleClick = () => {
      if (ad.link_url) {
        window.open(ad.link_url, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <div
        key={ad.id || index}
        style={{
          width: '100%',
          height: '200px',
          marginBottom: '0',
          overflow: 'hidden',
          borderRadius: '12px',
          cursor: ad.link_url ? 'pointer' : 'default',
          boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
          position: 'relative',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)';
        }}
        onClick={handleClick}
      >
        {isVideo ? (
          <video
            src={assetUrl}
            autoPlay
            loop
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : isImage ? (
          <img
            src={assetUrl}
            alt={ad.name || 'Advertisement'}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : null}
        
        {/* Ad Name and Description Overlay */}
        {/* {(ad.name || ad.description) && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              padding: '18px 14px 14px',
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.9) 100%)',
              color: '#ffffff',
              zIndex: 10,
              display: 'block',
              visibility: 'visible',
            }}
          >
            {ad.name && (
              <h4
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  margin: '0 0 6px 0',
                  textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                  lineHeight: '1.3',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: '#ffffff',
                }}
              >
                {ad.name}
              </h4>
            )}
            {ad.description && (
              <p
                style={{
                  fontSize: '14px',
                  margin: 0,
                  marginTop: ad.name ? '4px' : '0',
                  opacity: 1,
                  color: '#ffffff',
                  textShadow: '0 2px 6px rgba(0,0,0,0.8)',
                  lineHeight: '1.5',
                  visibility: 'visible',
                  overflow: 'hidden',
                  maxHeight: '42px',
                  wordWrap: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                }}
              >
                {ad.description}
              </p>
            )}
          </div>
        )} */}
      </div>
    );
  };

  return (
    <div style={rows === 1 ? {} : { padding: '40px 0', background: '#f8f9fa' }}>
      <div className="container">
        {/* Row 1: 3 ads */}
        <div className="row" style={{ margin: 0, marginBottom: rows > 1 ? '20px' : '0' }}>
          {row1.map((ad, index) => (
            <div key={ad.id || index} className="col-md-4" style={{ padding: rows === 1 ? '0' : '0 15px', marginBottom: rows === 1 ? '0' : '20px' }}>
              {renderAdItem(ad, index)}
            </div>
          ))}
        </div>
        {/* Row 2: 3 ads - only if rows > 1 */}
        {rows > 1 && row2.length > 0 && (
          <div className="row" style={{ margin: 0 }}>
            {row2.map((ad, index) => (
              <div key={ad.id || index + 3} className="col-md-4" style={{ padding: '0 15px', marginBottom: '20px' }}>
                {renderAdItem(ad, index + 3)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GridAds;

